import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Minimize2 } from "lucide-react";
import { api } from "@/lib/api";

export default function OriginalPreview({ detail, companyId, page, onPageChange }) {
  const panel = useRef(null);
  const [result, setResult] = useState({ state: "loading", url: null, page });
  const [pageCount, setPageCount] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [expanded, setExpanded] = useState(false);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  const mime = detail.storage_ref?.mime_type;
  const allowed = ["application/pdf", "image/png", "image/jpeg", "image/webp", "image/tiff"].includes(mime);
  const state = result.page === page ? result.state : "loading";

  useEffect(() => {
    const controller = new AbortController(); let objectUrl;
    setResult({ state: "loading", url: null, page });
    if (!allowed || !detail.storage_ref?.checksum) {
      setResult({ state: "unavailable", page, message: "Anteprima non disponibile per questo originale. Puoi consultare il testo o scaricare l’originale." });
      return () => controller.abort();
    }
    api.get(`/documents/${encodeURIComponent(detail.doc_id)}/preview`, {
      params: { page }, responseType: "blob", signal: controller.signal, headers: { "x-company-id": companyId },
    }).then((response) => {
      if (controller.signal.aborted) return;
      const count = Number(response.headers?.["x-dad-page-count"]);
      if (response.data?.type !== "image/png" || response.data.size > 8 * 1024 * 1024 || !Number.isInteger(count) || count < 1 || count > 1000 || response.headers?.["x-dad-original-checksum"] !== detail.storage_ref.checksum) {
        throw new Error("INVALID_PRIVATE_PREVIEW");
      }
      objectUrl = URL.createObjectURL(response.data);
      setPageCount(count); setResult({ state: "ready", url: objectUrl, page });
    }).catch(async (error) => {
      if (controller.signal.aborted) return;
      let message = "Anteprima non disponibile. Il testo resta consultabile; puoi riprovare o scaricare l’originale.";
      if ([401, 403, 404].includes(error.response?.status)) message = "Anteprima non disponibile per questa sessione o azienda. Verifica l’accesso al documento.";
      if (error.response?.status === 422) {
        try { const body = JSON.parse(await error.response.data.text()); if (typeof body.detail === "string") message = body.detail; } catch {}
      }
      if (!controller.signal.aborted) setResult({ state: "unavailable", page, message });
    });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [detail.doc_id, companyId, page, allowed, detail.storage_ref?.checksum]);

  useEffect(() => {
    const node = panel.current;
    const changed = () => setNativeFullscreen(document.fullscreenElement === node);
    const escape = (event) => { if (event.key === "Escape" && expanded) { event.preventDefault(); setExpanded(false); } };
    document.addEventListener("fullscreenchange", changed); document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("fullscreenchange", changed); document.removeEventListener("keydown", escape);
      if (document.fullscreenElement === node) document.exitFullscreen?.().catch(() => {});
    };
  }, [expanded]);

  const fullscreen = async () => {
    if (expanded) { setExpanded(false); return; }
    if (document.fullscreenElement === panel.current) { await document.exitFullscreen?.(); return; }
    try {
      if (!panel.current.requestFullscreen) { setExpanded(true); return; }
      await panel.current.requestFullscreen();
    } catch { setExpanded(true); }
  };

  return (
    <section ref={panel} data-testid="original-preview" data-state={state} data-fullscreen={expanded || nativeFullscreen}
      className={`flex flex-col bg-[#07101c] ${expanded ? "fixed inset-0 z-[80] p-4" : "h-full min-h-0"}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 border-b border-white/10 p-3">
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Pagina precedente" data-testid="preview-previous" disabled={page <= 1 || state !== "ready"} onClick={() => onPageChange(page - 1)} className="btn-ghost !p-2 disabled:opacity-30"><ChevronLeft size={16} /></button>
          <label className="text-xs text-gray-300">Pagina <input data-testid="preview-page" aria-label="Pagina originale" type="number" min="1" max={pageCount} value={page}
            onChange={(event) => { const value = Number(event.target.value); if (Number.isInteger(value) && value >= 1 && value <= pageCount) onPageChange(value); }}
            className="w-12 bg-transparent border border-white/20 rounded p-1 mx-1" /> di {pageCount}</label>
          <button type="button" aria-label="Pagina successiva" data-testid="preview-next" disabled={page >= pageCount || state !== "ready"} onClick={() => onPageChange(page + 1)} className="btn-ghost !p-2 disabled:opacity-30"><ChevronRight size={16} /></button>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Riduci zoom" disabled={zoom <= 50} onClick={() => setZoom((value) => Math.max(50, value - 25))} className="btn-ghost !p-2"><ZoomOut size={16} /></button>
          <span className="text-xs text-gray-300" data-testid="preview-zoom">{zoom}%</span>
          <button type="button" aria-label="Aumenta zoom" disabled={zoom >= 300} onClick={() => setZoom((value) => Math.min(300, value + 25))} className="btn-ghost !p-2"><ZoomIn size={16} /></button>
          <button type="button" aria-label={expanded || nativeFullscreen ? "Esci da schermo intero" : "Apri anteprima a schermo intero"} onClick={fullscreen} className="btn-ghost !p-2">
            {expanded || nativeFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-3" aria-busy={state === "loading"}>
        {state === "loading" && <p role="status" className="text-sm text-gray-400">Caricamento dell’originale attraverso l’accesso aziendale…</p>}
        {state === "unavailable" && <p role="status" data-testid="preview-unavailable" className="text-sm text-amber-200 p-4">{result.message}</p>}
        {state === "ready" && <img data-testid="original-preview-image" src={result.url} alt={`Originale ${detail.filename}, pagina ${page}`} draggable="false"
          style={{ width: `${zoom}%`, maxWidth: "none" }} className="block mx-auto bg-white shadow-xl" />}
      </div>
    </section>
  );
}
